import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  TvAlert,
  TvButton,
  TvCard,
  TvCardBody,
  TvDiv,
  TvI,
  TvLi,
  TvP,
  TvSmall,
  TvUl,
} from "@trustvid/design-system";
import {
  SMART_CAMPAIGN_INTRO,
  SMART_CAMPAIGN_MODES_HEADING,
  SMART_CAMPAIGN_START_MODES,
  SMART_CAMPAIGN_TIPS,
  getSmartCampaignModeStatusMessage,
} from "../constants/smartCampaignPromptGuide";

const AiCampaignInstructionsPanel = ({
  className = "mb-3",
  activeMode = "empty",
}) => {
  const [tipsOpen, setTipsOpen] = useState(false);
  const statusMessage = getSmartCampaignModeStatusMessage(activeMode);

  return (
    <TvDiv
      className={className}
      data-testid="ai-campaign-instructions-panel"
    >
      <TvAlert color="info" className="py-2 px-3 mb-3" data-testid="ai-campaign-intro-alert">
        <TvSmall className="mb-0 d-block">{SMART_CAMPAIGN_INTRO}</TvSmall>
      </TvAlert>

      <TvDiv className="mb-3" data-testid="ai-campaign-start-modes">
        <TvSmall className="text-muted fw-semibold d-block mb-2">{SMART_CAMPAIGN_MODES_HEADING}</TvSmall>
        <TvDiv className="d-flex flex-column flex-md-row gap-2">
          {SMART_CAMPAIGN_START_MODES.map((mode) => {
            const isActive = activeMode === mode.id;
            const cardClass = isActive ? "border-primary shadow-sm" : "border-light";
            return (
              <TvCard
                key={mode.id}
                className={`flex-fill mb-0 ${cardClass}`}
                data-testid={`ai-campaign-mode-${mode.id}`}
                data-active={isActive ? "true" : "false"}
              >
                <TvCardBody className="p-2 p-md-3">
                  <TvDiv className="d-flex align-items-start gap-2">
                    <TvI
                      className={`fa ${mode.icon} mt-1 ${isActive ? "text-primary" : "text-muted"}`}
                      aria-hidden="true"
                    />
                    <TvDiv>
                      <TvSmall className={`fw-semibold d-block ${isActive ? "text-primary" : ""}`}>
                        {mode.title}
                        {mode.recommended ? (
                          <TvSmall className="text-primary ms-1">(recommended)</TvSmall>
                        ) : null}
                      </TvSmall>
                      <TvSmall className="text-muted d-block">{mode.description}</TvSmall>
                    </TvDiv>
                  </TvDiv>
                </TvCardBody>
              </TvCard>
            );
          })}
        </TvDiv>
        {statusMessage ? (
          <TvSmall className="text-muted d-block mt-2" data-testid="ai-campaign-mode-status">
            {statusMessage}
          </TvSmall>
        ) : null}
      </TvDiv>

      <TvDiv className="d-flex align-items-center justify-content-between gap-2">
        <TvP className="text-muted small mb-0">
          Need more guidance?
        </TvP>
        <TvButton
          type="button"
          color="link"
          className="p-0 text-decoration-none"
          data-testid="ai-campaign-tips-toggle"
          onClick={() => setTipsOpen((open) => !open)}
          aria-expanded={tipsOpen}
        >
          {tipsOpen ? "Hide tips" : "Show tips"}
        </TvButton>
      </TvDiv>

      {tipsOpen ? (
        <TvUl
          className="mb-0 mt-2 ps-3 text-muted small"
          data-testid="ai-campaign-tips-list"
        >
          {SMART_CAMPAIGN_TIPS.map((tip) => (
            <TvLi key={tip}>{tip}</TvLi>
          ))}
        </TvUl>
      ) : null}
    </TvDiv>
  );
};

AiCampaignInstructionsPanel.propTypes = {
  className: PropTypes.string,
  activeMode: PropTypes.oneOf(["empty", "uploads-only", "prompt-only", "uploads-and-prompt"]),
};

export default AiCampaignInstructionsPanel;
